import { useEffect } from 'react'

import Box from '@mui/material/Box'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { useLocation, useNavigate, useParams } from 'react-router'
import { Outlet } from 'react-router'

import { HRAdminPermission, STRETCH_FULL_HEIGHT } from 'app/lib/constants'
import { useIsSuperAdmin, useUserPermissions } from 'app/lib/hoc/withProtectedComponent'
import { useTrackPageView } from 'app/lib/hooks/use-track-page-view'
import { OptionalOrganizationRoute } from 'app/models/scribe.models'
import { useGetOrganizationResourcesQuery, useListOrganizationsQuery } from 'app/redux/scribeApi'
import { getApplicationConfig } from 'config'

import { organizationMemberList } from '../lib/routes'

import { ApplicationSideBar } from './application-bar/ApplicationSideBar'
import { ApplicationTopBar } from './application-bar/ApplicationTopBar'
import { ZendeskWidget } from './ZendeskWidget'

export const Layout: React.FC = () => {
  const config = getApplicationConfig()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { data: organizationsResp, isUninitialized } = useListOrganizationsQuery({
    is_brand_organization: false,
  })
  const hasOnlyOneOrganization = organizationsResp?.data?.length === 1
  const userPermissions = useUserPermissions()
  const isSuperAdmin = useIsSuperAdmin()
  const { supportChatbot: supportChatbotFlag } = useFlags()
  const sideBarBackgroundColor = config.theme.applicationBar.backgroundColor

  // This executes only on the initial render. If the user has
  // access to only one organization, redirect to that organization.
  useEffect(() => {
    if (hasOnlyOneOrganization) {
      navigate(organizationMemberList.get(organizationsResp.data[0].id), { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasOnlyOneOrganization])

  // Scroll to top each time the url path changes. (By default the browser never
  // resets the scrolling position even when the path changes)
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [pathname])

  // list:report identifies HR Admins
  const showZendeskWidget =
    !window.Cypress &&
    supportChatbotFlag &&
    (isSuperAdmin || userPermissions?.includes(HRAdminPermission.LIST_REPORT))

  if (isUninitialized) {
    return null // wait to have organizations, to prevent flickering
  }
  return (
    <Box sx={{ display: 'flex', flex: 1 }}>
      <ApplicationTopBar backgroundColor={sideBarBackgroundColor} />
      <SideBar backgroundColor={sideBarBackgroundColor} hideBackButton={hasOnlyOneOrganization} />
      <Box component="main" sx={{ flexGrow: 1, py: 4, px: 4, mt: 8, ...STRETCH_FULL_HEIGHT }}>
        <PageViewTracker />
        <Outlet />
      </Box>
      {showZendeskWidget && <ZendeskWidget />}
    </Box>
  )
}

// component rendered after the isUninitialized early return
// so page views hook fires once the page shows
const PageViewTracker = () => {
  useTrackPageView()
  return null
}

interface SideBarProps {
  backgroundColor: string
  hideBackButton: boolean
}

const SideBar = ({ backgroundColor, hideBackButton }: SideBarProps) => {
  const { organizationId } = useParams() as OptionalOrganizationRoute
  const { data: resources } = useGetOrganizationResourcesQuery(organizationId ?? skipToken)

  const isSuperAdmin = useIsSuperAdmin()
  const displayEngagementResourcesTab = isSuperAdmin || !!resources?.[0]?.resourceAsset

  return (
    <ApplicationSideBar
      backgroundColor={backgroundColor}
      hideBackButton={hideBackButton}
      displayEngagementResourcesTab={displayEngagementResourcesTab}
      organizationId={organizationId}
    />
  )
}
